import { Component, OnChanges, OnDestroy, SimpleChange, SimpleChanges } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { PlacePanel } from '@unpispas/upp-base';
import { PriceInfo, PriceType } from '@unpispas/upp-defs';
import { ViewRenderer } from '@unpispas/upp-wdgt';

import { alertService } from '@unpispas/upp-base';
import { deviceService } from '@unpispas/upp-base';
import { languageService } from '@unpispas/upp-base';
import { WSConnection } from '@unpispas/upp-base';
import { dataService } from '@unpispas/upp-data';
import { i18nSupport } from '@unpispas/upp-data';
import { viewService } from '@unpispas/upp-base';

import { ScaleDevice } from '@unpispas/upp-data';

import { PlaceView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';
import { ToCartInfo } from '@unpispas/upp-data';
import { CategoryView } from '@unpispas/upp-data';
import { ProductView } from '@unpispas/upp-data';
import { ProductOptView } from '@unpispas/upp-data';
import { TicketProductView } from '@unpispas/upp-data';

class Selection {
    constructor(public product: ProductView, private ProductForm: FormGroup, private change: ViewRenderer){
        // nothing to do
    }

    get comments(): string | null {
        return this.ProductForm.get('comments')?.value || null;
    }

    public _itemstocart = 1;
    get tocart(): number{
        return this._itemstocart;
    }

    set tocart(value: number){
        this._itemstocart = value;
        this.change.markForCheck();
    }

    private _iscompleted: boolean | null = null;
    get IsCompleted(): boolean {
        if (this._iscompleted === null){
            this._iscompleted = this._IsCompleted;

            // avoid recalculating until next cycle
            setTimeout(() => {
                this._iscompleted = null;
            }, 0);
        }
        return this._iscompleted;
    }

    get options(): Array <ProductOptView> {
        const _options: Array <ProductOptView> = [];
        for(const _option of this.product.options){
            if (_option.Checked && _option.IsAvailable){
                _options.push(_option);
            }
        }
        return _options;
    }

    get priceinfo(): PriceType {
        if (!this.IsCompleted){
            return new PriceInfo(0, 0).price;;
        }

        let _p = this.product.price || 0;
        if (this.product.IsGeneric || this.product.IsVarious){
            _p = Number(this.ProductForm.get('price')?.value || 0);
        }
        else if (this.product.IsMarket){
            _p = Number(this.ProductForm.get('market')?.value || 0);
        }

        for (const _option of this.options){
            _p += _option.price || 0;
        }

        if (this.product.IsWeighted){
            const _weight = Number(this.ProductForm.get('weight')?.value || 0);
            _p = _p * (_weight / 1000);
        }

        const _r = this.product.taxrate;
        return new PriceInfo(_p, _r).price;
    } 

    /*******************************/
    /* PRIVATE METHODS             */
    /*******************************/

    private get _IsCompleted(): boolean {
        if (!this.product || !this.ProductForm.valid){
            return false;
        }

        if (this.product.options.length == 0){
            return true;   // no options in this product
        }

        for (const _category of this.product.categories){
            if (!_category.Suitable){
                continue;
            }

            let _categoryCompleted = !_category.IsAvailable;

            if (_category.type == 'SINGLE'){
                const _size: number = _category.Selected.filter(option => option.IsAvailable).length;
                _categoryCompleted = _categoryCompleted || (_size == 1);
            }
            else if (_category.type == 'FIXED'){
                const _size: number = _category.Selected.filter(option => option.IsAvailable).length;
                _categoryCompleted = _categoryCompleted || ((_size >= (_category.fxmin || 0)) && (_size <= (_category.fxmax || 0)));
            }
            else {
                _categoryCompleted = true;
            }

            if (!_categoryCompleted){
                return false;
            }
        }

        return true;
    }    
}

@Component({
    selector: 'upp-product-cart',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-cart.html',
    styleUrls: [
      './upp-cart.scss'
    ],
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef]
    }]       
})
export class UppProductCartComponent extends i18nSupport implements OnChanges, OnDestroy {
    @Input() place: PlaceView | null = null;
    @Input() product: ProductView | null = null;
    @Input() options: Array <ProductOptView> | null = null;
    @Input() ticket: TicketView | null = null;
    @Input() ticketproduct: TicketProductView | null = null;
    /**
     * Units of this catalog product already in the ticket cart.
     * When > 0 (and not weighted), the stepper starts at that count and
     * accept syncs the cart to the chosen amount (add or remove).
     */
    @Input() incart: number | null = null;

    @Output() ToPanel = new EventEmitter <PlacePanel> ();

    private ContentDirty = false;

    constructor(
        private lang: languageService,
        private change: ViewRenderer,
        private alertCtrl: alertService,
        private data: dataService,
        private devices: deviceService,
        public view: viewService
    ){
        super(lang, change.cdref);
    }

    private _resetForm(){
        this._expandedoptions.clear();
        this._selection = null; 

        if (this.product){
            this.product.ResetChecked();

            if (this.options){
                for (const _option of this.options){
                    if (_option.IsAvailable){
                        _option.Checked = true;
                    }
                }
            }        
        }
    }

    public ChangeUpdate = false;
    ngOnChanges(changes: SimpleChanges){
        // Reset only when product/line identity changes (or first bind). Parent CD and
        // ticket Copy refreshes rebind place/ticket/options without a new catalog row;
        // rebuilding the form from catalog nulls would clear order-time price/type.
        const identityChanged =
            this._InputIdentityChanged(changes['product']) ||
            this._InputIdentityChanged(changes['ticketproduct']);

        if (identityChanged || this._productForm == null){
            this._resetForm();
            this._productForm = this.LoadProductForm();
        }

        if (changes['place'] || changes['product']){
            void this._RefreshScaleConnection();
        }

        setTimeout(() => {
            this.ChangeUpdate = false;
            this.change.markForCheck();
        }, 0);
    }

    /**
     * True on first bind or when the logical `@Input` row identity changed.
     * Compares `.object` with `IsEqual` so a `Copy()`/`CopyOf` of the same row
     * does not count as a new product/line.
     */
    private _InputIdentityChanged(change: SimpleChange | undefined): boolean {
        if (!change){
            return false;
        }

        if (change.firstChange){
            return true;
        }

        const _prev = change.previousValue?.object ?? null;
        const _next = change.currentValue?.object ?? null;

        if (_prev == null && _next == null){
            return false;
        }

        if (_prev == null || _next == null){
            return true;
        }

        return !_prev.IsEqual(_next);
    }

    ngOnDestroy(): void {
        this._WSClose();
        super.OnDestroy();
    }

    /********************************/
    /* PRODUCT FORM                 */
    /********************************/

    private _productForm: FormGroup | null = null;   
    private LoadProductForm(): FormGroup {
        const _market: number | null = this.ticketproduct?.market ?? null;
        const _weight: number | null = this.ticketproduct?.weight ?? null;
        // Generic/various price and prep type are per order (ticket line), never catalog.
        const _orderPriced = !!(this.product && (this.product.IsGeneric || this.product.IsVarious));
        const _price: number | null = _orderPriced
            ? (this.ticketproduct?.__fixedprice ?? null)
            : (this.product?.price || null);
        const _typeSource = _orderPriced
            ? (this.ticketproduct?.type || null)
            : (this.product?.type || null);
        const _type: string | null = ['BAR', 'KIT'].includes(_typeSource || '') ? _typeSource : null;
        const _comments: string | null = this.ticketproduct?.comments ?? null;

        let _form: FormGroup | null = this._productForm;
        if (_form == null){
            _form = new FormGroup({
                market: new FormControl(_market, [ 
                    Validators.required
                ]),
                weight: new FormControl(_weight, [ 
                    Validators.required
                ]),
                price: new FormControl(_price, [
                    Validators.required
                ]),
                type: new FormControl(_type, [
                    Validators.required
                ]),
                comments: new FormControl(_comments, [ 
                    // no validators required
                ])
            });
        }
        else {
            _form.patchValue({
                market: _market,
                weight: _weight,
                price: _price,
                type: _type,
                comments: _comments
            });
        }

        // adjust validators based on product configuration
        if (this.product && !this.product.IsMarket){
            _form.get('market')?.clearValidators();
        }
        else {
            _form.get('market')?.setValidators([ 
                Validators.required 
            ]);
        }

        if (this.product && !this.product.IsWeighted){
            _form.get('weight')?.clearValidators();
        }
        else {
            _form.get('weight')?.setValidators([ 
                Validators.required 
            ]);
        }

        if (this.product && !(this.product.IsGeneric || this.product.IsVarious)){
            _form.get('price')?.clearValidators();
            _form.get('type')?.clearValidators();
        }
        else {
            _form.get('price')?.setValidators([ 
                Validators.required 
            ]);
            _form.get('type')?.setValidators([ 
                Validators.required 
            ]);
        }

        _form.get('market')?.updateValueAndValidity({ emitEvent: false });
        _form.get('weight')?.updateValueAndValidity({ emitEvent: false });
        _form.get('price')?.updateValueAndValidity({ emitEvent: false });
        _form.get('type')?.updateValueAndValidity({ emitEvent: false });

        this.change.markForCheck();
        
        _form.markAsPristine();
        setTimeout(() => {
            _form?.markAsPristine();
        }, 0)    
        
        return _form;
    }

    private _status_break = false;
    get ProductForm(): FormGroup {
        if (this._productForm == null){
            this._productForm = this.LoadProductForm();

            if (this._status_break){
                this._productForm.statusChanges.subscribe(status => {
                    console.log('Form status changed:', status, 'Pristine:', this._productForm?.pristine);
                });                
            }
        }
        return this._productForm;        
    }   

    /********************************/
    /* SCALE (resolved for widget)  */
    /********************************/

    private _wsConnection: WSConnection | null = null;
    private _wsToken = 0;

    /**
     * First valid scale on the session service.
     */
    get scale(): ScaleDevice | null {
        for (const _scale of this.place?.service?.scales || []){
            if (_scale.IsValid){
                return _scale;
            }
        }

        return null;
    }

    /** Open WS for the scale widget (`null` when unavailable). */
    get connection(): WSConnection | null {
        return this._wsConnection;
    }

    private async _RefreshScaleConnection(): Promise<void> {
        this._WSClose();

        if (!this.product?.IsWeighted || !this.scale || !this.place?.service){
            this.change.markForCheck();
            return;
        }

        const _token = ++this._wsToken;
        const _connection = await this.place.service.WSOpen(this.devices, { local: true });
        if (_token != this._wsToken){
            if (_connection){
                this.place.service.WSClose(_connection);
            }
            return;
        }

        this._wsConnection = _connection;
        this.change.markForCheck();
    }

    private _WSClose(): void {
        const _connection = this._wsConnection;
        this._wsConnection = null;
        this._wsToken++;

        if (!_connection){
            return;
        }

        if (this.place?.service){
            this.place.service.WSClose(_connection);
            return;
        }

        _connection.Disconnect();
    }

    /********************************/
    /* OPTION DESCRIPTIONS          */
    /********************************/ 

    private _expandedoptions = new Set <ProductOptView> ();
    ExpandedOption(option: ProductOptView){
        return this._expandedoptions.has(option);
    }

    /**
     * Toggles expanded help row for an option description (with or without text).
     */
    OnOptionExpand(event: Event, option: ProductOptView){
        event.stopPropagation();
        event.preventDefault();

        if (this._expandedoptions.has(option)){
            this._expandedoptions.delete(option);
        }
        else {
            this._expandedoptions.add(option);
        }

        this.change.markForCheck();
    }    

    /********************************/
    /* ITEMS TO CART                */
    /********************************/ 

    get ItemSize(): number {
        return this.select?.tocart ?? 0;
    }

    get ItemUnits(): string {
        if (this.product && this.product.IsWeighted){
            return this.lang.tr('@kilograms');
        }
        return '';
    }

    /**
     * Re-open with units already in cart: stepper shows that count and accept
     * sets the cart to the chosen amount (including zero to remove all).
     */
    get IsCartSync(): boolean {
        return !!this.product && !this.product.IsWeighted && (this.incart ?? 0) > 0;
    }

    /**
     * Label for the accept/submit button. Always includes the chosen unit
     * count so the guest sees how many are selected (including cart-sync).
     * Weighted products hide the counter: it would read as a weight (e.g.
     * "1kg") that does not match the value typed/captured in the weight field.
     */
    get ItemAcceptLabel(): string {
        if (this.product?.IsWeighted){
            return this.lang.tr('@ticket_product_add_weighted');
        }

        return this.lang.tr('@ticket_product_add_accept', [ this.ItemSize, this.ItemUnits ]);
    }

    get CanAddItem(): boolean {
        if (!this.select || !this.select.IsCompleted){
            return false;
        }

        return (this.ItemSize < 99)
    }

    get CanDelItem(): boolean {
        if (!this.select){
            return false;
        }

        if (this.IsCartSync){
            return this.ItemSize > 0;
        }

        if (!this.select.IsCompleted){
            return false;
        }

        return (this.ItemSize > 1)        
    }

    OnAddItem(event: Event){
        event.stopPropagation();
        if (this.select && this.CanAddItem){
            this.select.tocart++;
            this.change.markForCheck();    
        }
    }

    OnDelItem(event: Event){
        event.stopPropagation();
        if (this.select && this.CanDelItem){
            this.select.tocart--;
            this.change.markForCheck();    
        }
    }

    get CanAccept(): boolean {
        if (!this.select){
            return false;
        }

        if (this.IsCartSync && this.ItemSize === 0){
            return true;
        }

        return this.select.IsCompleted;
    }

    private _ProductInfo(): ToCartInfo | null {
        const comments = this.select?.comments || null;
        const info: ToCartInfo = { comments };

        if (this.product?.IsMarket){
            const market = Number(this.ProductForm.get('market')?.value);
            info.market = Number.isFinite(market) ? market : null;
        }

        if (this.product?.IsWeighted){
            const weight = Number(this.ProductForm.get('weight')?.value);
            info.weight = Number.isFinite(weight) ? weight : null;
        }

        if (this.product && !(this.product.IsGeneric || this.product.IsVarious)){
            return info;
        }

        const genericPrice = Number(this.ProductForm.get('price')?.value ?? 0);
        const genericType = this.ProductForm.get('type')?.value as 'BAR' | 'KIT' | null;
        if (!Number.isFinite(genericPrice) || !genericType || !['BAR', 'KIT'].includes(genericType)){
            return null;
        }

        info.fixed = genericPrice;
        info.type = genericType;
        return info;
    }

    OnInsert(){
        if (!this.ticket || !this.product || !this.select){
            return;
        }

        if (this.IsCartSync){
            this._SyncCartUnits();
            return;
        }

        const productInfo = this._ProductInfo();
        if (!productInfo){
            return;
        }

        const productOptions = (this.product.IsGeneric || this.product.IsVarious) ? [] : this.select.options;
        this.ticket.ToCart(this.product, productOptions, this.select.tocart, productInfo);
        this._OnClose();
    }

    /**
     * Align InCart units of this catalog product with the stepper value.
     * Extra units are added via {@link TicketView.ToCart}; surplus lines are
     * reduced with {@link TicketView.SetAmount} (largest lines first).
     */
    private _SyncCartUnits(): void {
        if (!this.ticket || !this.product || !this.select){
            return;
        }

        const target = Math.max(0, this.select.tocart);
        const lines = this.ticket.products.filter(
            (line) => line.InCart && line.product === this.product,
        );

        let current = 0;
        for (const line of lines){
            current += line.Count || 0;
        }

        if (target === current){
            this._OnClose();
            return;
        }

        if (target > current){
            const productInfo = this._ProductInfo();
            if (!productInfo){
                return;
            }

            const productOptions = (this.product.IsGeneric || this.product.IsVarious) ? [] : this.select.options;
            this.ticket.ToCart(this.product, productOptions, target - current, productInfo);
            this._OnClose();
            return;
        }

        let need = current - target;
        const ordered = lines.slice().sort((a, b) => (b.Count || 0) - (a.Count || 0));
        for (const line of ordered){
            if (need <= 0){
                break;
            }

            const count = line.Count || 0;
            if (count <= 0){
                continue;
            }

            const take = Math.min(need, count);
            this.ticket.SetAmount(line, count - take);
            need -= take;
        }

        this._OnClose();
    }

    OnUpdate(): void {
        if (!this.ticket || !this.product || !this.select || !this.ticketproduct){
            return;
        }

        const productInfo = this._ProductInfo();
        if (!productInfo){
            return;
        }

        const replaceAmount = this.ticketproduct.amount || 0;
        if (replaceAmount <= 0){
            return;
        }

        const productOptions = (this.product.IsGeneric || this.product.IsVarious) ? [] : this.select.options;

        this.ticketproduct.amount = 0;
        this.ticketproduct.addlines = Math.round(Math.max(0, (this.ticketproduct.addlines ?? 0) - replaceAmount) * 100) / 100;

        this.ticket.ToCart(this.product, productOptions, replaceAmount, productInfo);
        this._OnClose();
    }

    OnAccept(){
        this.OnInsert();
    }

    /********************************/
    /* DISPLAY OPTIONS              */
    /********************************/ 

    CategoryTrack(index: number, category: CategoryView): any {
        return category?._uuid || null;
    }

    OptionTrack(index: number, option: ProductOptView): any {
        return option?._uuid || null;
    }

    private _selection: Selection | null = null;
    get select(): Selection | null {
        if (!this.product){
            return null;
        }

        if (this._selection === null){
            this._selection = new Selection(this.product, this.ProductForm, this.change);
            if (this.IsCartSync){
                this._selection.tocart = this.incart ?? 1;
            }
        }

        return this._selection;
    }

    /********************************/
    /* EVENT HANDLERS               */
    /********************************/  

    private _OnClose(){
        this.ContentDirty = false;
        this.ProductForm.markAsPristine();

        this.ToPanel.emit({
            scnd: 'ticket',
            args: {
                ticket: this.ticket
            },
            force: true
        });
    }

    async OnClose(){
        if (this.ProductForm.dirty || this.ContentDirty){
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@changes_lost_confirm_tittle'),
                message: this.lang.tr('@changes_lost_confirm_message'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
    
                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    }
                }]
            });
    
            alert.onDidDismiss().then((detail:any) => {
                if (detail.data == true){
                    this._OnClose();
                }
            });
    
            await alert.present();    
        }
        else {
            this._OnClose();
        }
    }

    /**
     * Applies SINGLE-category radio selection; ignores unavailable options.
     */
    OnSingleSelected(event: CustomEvent, category: CategoryView): void {
        const _option = event.detail.value as ProductOptView;
        if (!_option?.IsAvailable){
            return;
        }

        category.SingleOption = _option;
        this.ContentDirty = true;
    }

    /**
     * Applies checkbox selection for non-SINGLE categories; clears unavailable options.
     */
    OnOptionSelected(event: CustomEvent, option: ProductOptView){
        if (!option.IsAvailable){
            option.Checked = false;
            return;
        }

        option.Checked = event.detail.checked;
        this.ContentDirty = true;
    }
}
